/**
 * math.ts — typed wrapper around the global MathJax (loaded via the
 * <script> tag in index.html from the self-hosted public/mathjax/ bundle).
 *
 * Why a wrapper: MathJax's browser bundle loads asynchronously, so a slider
 * `input` event can fire before `window.MathJax` is fully started up. These
 * helpers wait for readiness before typesetting, and expose a single `render`
 * that swaps an element's TeX source and re-typesets it in one call — the
 * pattern the draft used for the live-updating slope equation.
 *
 * No graceful degradation: if MathJax never loads, the TeX source stays
 * visible as raw text (e.g. `$$ m = ... $$`), which is acceptable for a demo.
 */

// Minimal subset of the MathJax v3 global we depend on. The full shape is
// large and not shipped as types; we declare only what we call.
interface MathJaxTypesetOptions {
  elements?: HTMLElement[];
}
interface MathJaxGlobal {
  startup: {
    promise: Promise<void>;
    typeset: (elements?: HTMLElement[]) => Promise<void>;
  };
  typesetPromise?: (elements?: (HTMLElement | MathJaxTypesetOptions)[]) => Promise<void>;
  // Re-typeset a specific element after its content changed.
  typesetClear?: (elements: HTMLElement[]) => void;
}

declare global {
  interface Window {
    MathJax?: MathJaxGlobal;
  }
}

/**
 * Resolves once MathJax has finished its startup sequence (fonts loaded,
 * initial typeset done). If MathJax is absent or never starts, rejects after
 * `timeoutMs` so callers can fall back to showing raw TeX rather than hang.
 */
export function mathJaxReady(timeoutMs = 8000): Promise<void> {
  return new Promise((resolve, reject) => {
    const mj = window.MathJax;
    if (mj?.startup?.promise) {
      // Race the startup promise against the timeout so a stuck bundle can't
      // wedge the page forever.
      Promise.race([
        mj.startup.promise,
        new Promise<void>((_, t) => setTimeout(() => t(new Error('MathJax startup timeout')), timeoutMs)),
      ]).then(resolve, reject);
      return;
    }
    // Bundle loaded but not yet initialised, or still downloading. Poll until
    // the startup promise appears (MathJax assigns window.MathJax before its
    // startup sequence, then augments it once the bundle runs).
    const start = performance.now();
    const id = window.setInterval(() => {
      const current = window.MathJax;
      if (current?.startup?.promise) {
        window.clearInterval(id);
        Promise.race([
          current.startup.promise,
          new Promise<void>((_, t) =>
            setTimeout(() => t(new Error('MathJax startup timeout')), timeoutMs),
          ),
        ]).then(resolve, reject);
      } else if (performance.now() - start > timeoutMs) {
        window.clearInterval(id);
        reject(new Error('MathJax did not load'));
      }
    }, 50);
  });
}

let readyPromise: Promise<void> | null = null;
function ensureReady(): Promise<void> {
  if (!readyPromise) {
    readyPromise = mathJaxReady().catch((err) => {
      // Log once; let subsequent typeset() calls no-op rather than re-throwing.
      console.warn('[math]', err.message);
      readyPromise = null;
    });
  }
  return readyPromise;
}

/**
 * Re-typeset already-rendered elements (or the whole document if omitted).
 * Safe to call repeatedly from a slider `input` handler.
 */
export async function typeset(elements?: HTMLElement[]): Promise<void> {
  try {
    await ensureReady();
    const mj = window.MathJax;
    if (!mj) return;
    if (mj.typesetPromise) {
      await mj.typesetPromise(elements);
    } else if (mj.startup?.typeset) {
      await mj.startup.typeset(elements);
    }
  } catch {
    /* Swallow — raw TeX remains visible, which is fine for a demo. */
  }
}

/**
 * Replace an element's TeX source and re-typeset just that element. Returns
 * once MathJax has updated the DOM, so a caller can chain further work.
 */
export async function render(el: HTMLElement, tex: string): Promise<void> {
  el.textContent = tex;
  await typeset([el]);
}
